import { QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect } from 'react';

type UseLongPollingQueryOptions<TData = unknown> = {
	enabled?: boolean;
	onMessage?: (data: TData) => void;
};

export const useLongPollingQuery = <
	TQueryFnData = unknown,
	TError = AxiosError<any>,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey
>(
	queryKey: TQueryKey,
	queryFn: ({ params, signal }: { params: any; page?: any; signal: AbortSignal }) => Promise<TQueryFnData>,
	options?: UseLongPollingQueryOptions<TData>
) => {
	const queryClient = useQueryClient();

	const {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		data: _data,
		refetch,
		remove,
		error,
		status,
	} = useQuery<TQueryFnData, AxiosError, TData, TQueryKey>(
		queryKey,
		({ pageParam, queryKey, signal }) => queryFn({ params: queryKey[1], page: pageParam, signal: signal! }),
		{
			enabled: false,
			retry: false,
			onSuccess: (data) => {
				if (data) {
					options?.onMessage?.(data);
				}
			},
		}
	);

	useEffect(() => {
		const isAborted = error?.code === 'ECONNABORTED';
		const isTimeout = error?.response?.status === 504 || error?.response?.status === 408;
		const isNoContent = error?.response?.status === 204;
		const isEmptyResponse = status === 'success' && _data === '';
		if (isEmptyResponse || isAborted || isTimeout || isNoContent) {
			remove();
			refetch({ throwOnError: false, type: 'all' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [status, error]);

	useEffect(() => {
		if (_data) {
			remove();
			refetch({ throwOnError: false, type: 'all' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [_data]);

	useEffect(() => {
		if (options?.enabled !== undefined) {
			if (options?.enabled === false) {
				remove();
			} else {
				refetch({ throwOnError: false, type: 'all' });
			}
		} else {
			refetch();
		}
		return () => {
			remove();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [options?.enabled]);

	const cancel = useCallback(() => {
		queryClient.cancelQueries(queryKey);
		remove();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey]);

	const data = _data === '' ? null : _data;

	return { data, cancel };
};
